<script setup>
/**
 * A miniature chat-widget rendering of the campaign message, so the dashboard
 * card shows what the customer will actually see. Decorative only — the real
 * message text is announced by the card itself.
 */
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { getContrastingTextColor } from '@chatwoot/utils';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';
import { DEFAULT_THEME_COLOR } from 'dashboard/components-next/Campaigns/helpers/campaignContent';

const props = defineProps({
  title: {
    type: String,
    default: '',
  },
  message: {
    type: String,
    default: '',
  },
  /** `content.theme_color`; empty falls back to the product default. */
  themeColor: {
    type: String,
    default: '',
  },
});

const { t } = useI18n();
const { formatMessage } = useMessageFormatter();

const ctaStyle = computed(() => {
  const color = props.themeColor || DEFAULT_THEME_COLOR;
  return { backgroundColor: color, color: getContrastingTextColor(color) };
});
</script>

<template>
  <div class="px-5 pt-5 pb-4 bg-n-alpha-1" aria-hidden="true">
    <div
      class="flex flex-col gap-1 p-3 max-w-64 shadow-sm rounded-xl rounded-bl-sm bg-n-solid-1 outline outline-1 -outline-offset-1 outline-n-container"
    >
      <span class="text-sm font-semibold text-n-slate-12 line-clamp-1">
        {{ title }}
      </span>
      <div
        v-if="message"
        v-dompurify-html="formatMessage(message, false, false, false)"
        class="text-xs text-n-slate-11 line-clamp-2 [&>p]:mb-0"
      />
      <span
        class="self-start inline-flex items-center h-6 px-2.5 mt-1.5 font-medium rounded-full text-label-small"
        :style="ctaStyle"
      >
        {{ t('CAMPAIGN.ALL.CARD.PREVIEW_CTA') }}
      </span>
    </div>
  </div>
</template>
